/**
 * useProcessingListener — Firestore onSnapshot for processing progress
 *
 * Reads processing status written to the document by the v3 backend.
 *
 * See: docs/features/integrations/deepgram-prerecorded.md
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../utils/firebase";

/**
 * @param {Object} params
 * @param {string} params.patientId   — Current patient ID
 * @param {string} params.documentId  — Document being processed
 * @param {Object} [params.callbacks] — Optional transition callbacks
 * @param {Function} [params.callbacks.onContinue] — Fired when sections complete
 * @param {Function} [params.callbacks.onComplete] — Fired when fully done
 * @param {Function} [params.callbacks.onError]    — Fired on error status
 */
export function useProcessingListener({ patientId, documentId, callbacks } = {}) {
  const [status, setStatus] = useState(null);
  const [continueReceived, setContinueReceived] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const [sectionsInitialized, setSectionsInitialized] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [processingError, setProcessingError] = useState(null);
  const [processingErrorCode, setProcessingErrorCode] = useState(null);

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Track previous status to fire callbacks only on transitions
  const prevStatusRef = useRef(null);

  const reset = useCallback(() => {
    setStatus(null);
    setContinueReceived(false);
    setIsComplete(false);
    setIsError(false);
    setSectionsInitialized(false);
    setProcessingProgress(null);
    setLastUpdated(null);
    setProcessingError(null);
    setProcessingErrorCode(null);
    prevStatusRef.current = null;
  }, []);

  useEffect(() => {
    if (!patientId || !documentId) return;

    const docRef = doc(db, `patients/${patientId}/documents`, documentId);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) return;

        const data = snapshot.data();
        const newStatus = data.status || null;

        setStatus(newStatus);
        setProcessingProgress(data.processingProgress || null);
        setLastUpdated(data.modifiedAt || null);
        setProcessingError(data.processingError || null);
        setProcessingErrorCode(data.processingErrorCode || null);

        // Track sections initialized
        if (data.sectionsInitialized && !sectionsInitialized) {
          setSectionsInitialized(true);
        }

        // Detect transitions and fire callbacks
        const prev = prevStatusRef.current;

        // continueReceived — sections complete, user can view doc
        if (data.continueReceived && !continueReceived) {
          setContinueReceived(true);
          callbacksRef.current?.onContinue?.();
        }

        // completed
        if (newStatus === "completed" && prev !== "completed") {
          setIsComplete(true);
          callbacksRef.current?.onComplete?.();
        }

        // error
        if (newStatus === "error" && prev !== "error") {
          setIsError(true);
          callbacksRef.current?.onError?.();
        }

        prevStatusRef.current = newStatus;
      },
      (error) => {
        console.error("[useProcessingListener] Snapshot error:", error);
        setIsError(true);
      }
    );

    return () => unsubscribe();
  }, [patientId, documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isProcessing = status === "processing" || status === "in progress";

  // Derive time-based progress fields
  const estimatedTotalMs = processingProgress?.estimatedTotalMs || 0;
  const startedAt = processingProgress?.startedAt || 0;

  return {
    status,
    continueReceived,
    isComplete,
    isError,
    isProcessing,
    sectionsInitialized,
    processingProgress,
    estimatedTotalMs,
    startedAt,
    lastUpdated,
    processingError,
    processingErrorCode,
    reset,
  };
}
